import * as Api from '@ViewModels';
import { inject, observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import { Switch } from 'react-router';
import { toast } from 'sonner';
import {
	IImpersonationContextComponentProps,
	IModalContext,
	ImpersonationContextKey,
	ModalChildComponentContextKey,
} from '../../../../../models';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useImageAttachmentMutation } from '../../../../../queries';
import { ImageFileChooserModal } from '../../../../components/ImageFileChooserModal';
import { MediaChooserModal } from '../../../../components/MediaChooser';
import { DefaultHighResHorizontalPixabayImageSearchOptions } from '../../../../components/MediaChooser/models';
import { IModalProps } from '../../../../components/Modal';
import { PrivateRoute } from '../../../../components/PrivateRoute';
import { EditSocialMediaSendOptions } from '../../../templates/EditSocialMediaSendOptions';
import { useEditSocialMediaPost } from '../hooks';
import { MobileEditSocialMediaPost } from './presentation';

interface MobileEditSocialMediaPostProps extends IImpersonationContextComponentProps, IModalContext {
	onPostUpdated?(): boolean;
}

const MobileEditSocialMediaPostContainerBase = (props: MobileEditSocialMediaPostProps) => {
	const { impersonationContext, parentModal, onPostUpdated } = props;

	const { context, errorMessages, h, isLoaded, postToEdit, routeMatch, scheduledPost, source, socialMediaConnections } =
		useEditSocialMediaPost({ impersonationContext });

	const [isSchedulingInProgress, setIsSchedulingInProgress] = React.useState(false);
	const userSession = useUserSession();
	const localErrorMessages = useErrorMessages();

	// File upload modal state
	const [fileInputModalOpen, setFileInputModalOpen] = React.useState(false);

	// Free images modal state
	const onFreeImageChooserRequestCloseRef = React.useRef<(img?: Api.IFileAttachment, cancel?: boolean) => void>(null);
	const [freeImageModalProps, setFreeImageModalProps] = React.useState<IModalProps>({
		isOpen: false,
		onRequestClose: (result, cancel) => onFreeImageChooserRequestCloseRef.current?.(result, cancel),
	});

	const modalContent = React.useMemo(() => {
		const content = context.postContent || '';
		return content.replace(/<[^>]*>?/gm, '');
	}, [context.postContent]);

	// Image upload mutation
	const imageAttachmentMutation = useImageAttachmentMutation({
		onError: (error: Api.IOperationResultNoValue): void => {
			localErrorMessages.pushApiError(error);
			toast.error('Failed to upload image');
		},
		onSuccess: result => {
			if (result && result.length > 0) {
				const currentImages = context.postImages || [];
				context.setImages([...currentImages, result[0]]);
				context.setHasChanges(true);
			}
		},
	});

	const onSelectedConnectionsChanged = React.useCallback(
		(selected: Api.IPostTarget[]) => {
			context.setPostTargets(selected);
			context.setHasChanges(true);
		},
		[context]
	);

	const onRequestClose = React.useCallback(() => {
		parentModal?.onRequestClose();
	}, [parentModal]);

	const onShowCancelUpdateConfirmation = React.useCallback(
		(e: React.MouseEvent<HTMLElement>) => {
			e.preventDefault();
			onRequestClose();
		},
		[onRequestClose]
	);

	const onScheduleForLater = React.useCallback(() => {
		h.push(`${routeMatch.url}/send-options`);
	}, [h, routeMatch.url]);

	const onPostNow = React.useCallback(async () => {
		if (isSchedulingInProgress) {
			return;
		}
		setIsSchedulingInProgress(true);
		try {
			const { schedulePost, updatePost } = context;
			// eslint-disable-next-line unicorn/prefer-ternary
			if (postToEdit || scheduledPost) {
				await updatePost(scheduledPost, moment());
			} else {
				await schedulePost(moment());
			}
			if (onPostUpdated?.()) {
				return;
			}
		} catch {
			// Error handling is done in context
		} finally {
			setIsSchedulingInProgress(false);
		}
	}, [context, isSchedulingInProgress, onPostUpdated, postToEdit, scheduledPost]);

	// File upload handlers
	const handleFileInputModalClose = React.useCallback(
		(val?: File | null, cancel?: boolean) => {
			setFileInputModalOpen(false);
			if (val && !cancel) {
				imageAttachmentMutation.mutate({ files: [val], impersonationContext });
			}
		},
		[imageAttachmentMutation, impersonationContext]
	);

	const onUploadMedia = React.useCallback(() => {
		setFileInputModalOpen(true);
	}, []);

	// Free images handlers
	const onFreeImages = React.useCallback(() => {
		setFreeImageModalProps(value => ({
			...value,
			isOpen: true,
		}));
	}, []);

	const onFreeImageChooserRequestClose = React.useCallback(
		(imageFile?: Api.IFileAttachment, cancel?: boolean) => {
			if (cancel || !imageFile) {
				setFreeImageModalProps(value => ({
					...value,
					isOpen: false,
				}));
				return;
			}

			const currentImages = context.postImages || [];
			context.setImages([...currentImages, imageFile]);
			context.setHasChanges(true);

			setFreeImageModalProps(value => ({
				...value,
				isOpen: false,
			}));
		},
		[context]
	);

	onFreeImageChooserRequestCloseRef.current = onFreeImageChooserRequestClose;

	const onSubmitPost = React.useCallback(
		async (_: React.MouseEvent<HTMLElement> | undefined, selectedSendTime?: moment.Moment) => {
			if (isSchedulingInProgress) {
				return;
			}
			setIsSchedulingInProgress(true);
			try {
				const { schedulePost, updatePost } = context;
				if (postToEdit || scheduledPost) {
					const [, error] = await updatePost(scheduledPost, selectedSendTime);
					if (error) {
						errorMessages.pushApiError(error);
						return;
					}
				} else {
					const [, error] = await schedulePost(selectedSendTime);
					if (error) {
						errorMessages.pushApiError(error);
						return;
					}
				}

				// Navigate back or close modal after successful scheduling
				if (onPostUpdated?.()) {
					return;
				}
				h.goBack();
			} catch {
				// Error handling is done in context
			} finally {
				setIsSchedulingInProgress(false);
			}
		},
		[context, errorMessages, h, isSchedulingInProgress, onPostUpdated, postToEdit, scheduledPost]
	);

	return (
		<>
			<Switch>
				<PrivateRoute path={`${routeMatch.url}/send-options`} userSession={userSession}>
					<EditSocialMediaSendOptions
						selectedUser={context?.selectedCreator}
						sendOnBehalf={
							(context?.selectedCreator?.id !== userSession.user.id && !impersonationContext?.isValid) ||
							impersonationContext?.isValid
						}
						initialStartDate={moment(postToEdit?.dueDate)}
						onSubmitPost={onSubmitPost}
						expirationDate={
							postToEdit?.status !== Api.PostStatus.Draft ? context?.template?.schedule?.expirationDate : null
						}
						templateId={routeMatch.params.templateId}
						isSchedulingInProgress={isSchedulingInProgress}
					/>
				</PrivateRoute>
				<PrivateRoute userSession={userSession}>
					<MobileEditSocialMediaPost
						context={context}
						socialMediaConnections={socialMediaConnections}
						onSelectedConnectionsChanged={onSelectedConnectionsChanged}
						onRequestClose={onRequestClose}
						onScheduleForLater={onScheduleForLater}
						onPostNow={onPostNow}
						onFreeImages={onFreeImages}
						onUploadMedia={onUploadMedia}
						onShowCancelUpdateConfirmation={onShowCancelUpdateConfirmation}
						isSchedulingInProgress={isSchedulingInProgress}
						postToEdit={postToEdit}
						scheduledPost={scheduledPost}
						source={source}
						errorMessages={errorMessages}
						isLoaded={isLoaded}
					/>
				</PrivateRoute>
			</Switch>

			{/* File upload modal */}
			<ImageFileChooserModal
				modalProps={{
					isOpen: fileInputModalOpen,
					onRequestClose: handleFileInputModalClose,
				}}
			/>

			{/* Free images modal */}
			{freeImageModalProps.isOpen && (
				<MediaChooserModal
					content={modalContent}
					imageOnly={true}
					impersonationContext={impersonationContext}
					modalProps={freeImageModalProps}
					onSave={onFreeImageChooserRequestClose}
					useBasicSearch={false}
					imageSearchOptions={DefaultHighResHorizontalPixabayImageSearchOptions}
				/>
			)}
		</>
	);
};

const MobileEditSocialMediaPostContainerBaseWithObserver = observer(MobileEditSocialMediaPostContainerBase);
export const MobileEditSocialMediaPostContainer = inject(
	ImpersonationContextKey,
	ModalChildComponentContextKey
)(MobileEditSocialMediaPostContainerBaseWithObserver);
